import type { ZodSchemaOf } from "@@/shared/zod-helpers";
import { z } from "zod";
import type { ProviderSlug } from "../providers/provider";

const destinations = {
    vouchers: "/vouchers",
    lacko: "/destinations/lacko",
    läckö: "/destinations/lacko",
    dafto: "/destinations/dafto",
    daftö: "/destinations/dafto",
    kronocamping: "/destinations/kronocamping",
    "smart-resande": "/destinations/smart-resande",
    "smart-resande-elbil": "/destinations/smart-resande-elbil",
    "smart-resande-skara": "/destinations/smart-resande-skara",
    "smart-resande-gotene": "/destinations/smart-resande-gotene",
    "smart-resande-lidkoping": "/destinations/smart-resande-lidkoping",
    "smart-resande-grastorp": "/destinations/smart-resande-grastorp",
    "smart-resande-vara": "/destinations/smart-resande-vara",
    "smart-resande-start": "/destinations/smart-resande-start",
    "naven-outdoor": "/destinations/naven-outdoor",
    "bauergarden-i-bunn": "/destinations/bauergarden-i-bunn",
    towni: "/",
    //zeta: "/home/boman",
};

const destinationNames = Object.keys(destinations) as DestinationName[];
type DestinationName = keyof typeof destinations;
const destinationNameZodSchema = z
    .string()
    .refine(value =>
        destinationNames.includes(value as DestinationName),
    ) as ZodSchemaOf<DestinationName>;
type DestinationTarget = {
    _type: "DESTINATION_TARGET";
} & (
    | {
          type: "LEGACY";
      }
    | {
          type: "PATH";
          path: string;
      }
    | {
          type: "PROVIDER_HOME";
          providerSlug: ProviderSlug;
      }
);
const towniTarget = [
    "towni",
    { _type: "DESTINATION_TARGET", type: "PATH", path: "/" },
] as const;
const destinationTargets = new Map<DestinationName, DestinationTarget>([
    [
        "vouchers",
        { _type: "DESTINATION_TARGET", type: "PATH", path: "/vouchers" },
    ],
    [
        "lacko",
        {
            _type: "DESTINATION_TARGET",
            type: "PROVIDER_HOME",
            providerSlug: "lacko" as ProviderSlug,
        },
    ],
    [
        "läckö",
        {
            _type: "DESTINATION_TARGET",
            type: "PROVIDER_HOME",
            providerSlug: "lacko" as ProviderSlug,
        },
    ],
    [
        "dafto",
        {
            _type: "DESTINATION_TARGET",
            type: "PROVIDER_HOME",
            providerSlug: "dafto-resort" as ProviderSlug,
        },
    ],
    [
        "daftö",
        {
            _type: "DESTINATION_TARGET",
            type: "PROVIDER_HOME",
            providerSlug: "dafto-resort" as ProviderSlug,
        },
    ],
    [
        "kronocamping",
        {
            _type: "DESTINATION_TARGET",
            type: "PROVIDER_HOME",
            providerSlug: "kronocamping" as ProviderSlug,
        },
    ],
    [
        "smart-resande",
        {
            _type: "DESTINATION_TARGET",
            type: "LEGACY",
        },
    ],
    [
        "smart-resande-elbil",
        {
            _type: "DESTINATION_TARGET",
            type: "LEGACY",
        },
    ],
    [
        "smart-resande-skara",
        {
            _type: "DESTINATION_TARGET",
            type: "LEGACY",
        },
    ],
    [
        "smart-resande-gotene",
        {
            _type: "DESTINATION_TARGET",
            type: "LEGACY",
        },
    ],
    [
        "smart-resande-lidkoping",
        {
            _type: "DESTINATION_TARGET",
            type: "LEGACY",
        },
    ],
    [
        "smart-resande-grastorp",
        {
            _type: "DESTINATION_TARGET",
            type: "LEGACY",
        },
    ],
    [
        "smart-resande-vara",
        {
            _type: "DESTINATION_TARGET",
            type: "LEGACY",
        },
    ],
    [
        "smart-resande-start",
        {
            _type: "DESTINATION_TARGET",
            type: "LEGACY",
        },
    ],
    [
        "naven-outdoor",
        {
            _type: "DESTINATION_TARGET",
            type: "PROVIDER_HOME",
            providerSlug: "naven-outdoor-experience" as ProviderSlug,
        },
    ],
    [
        "bauergarden-i-bunn",
        {
            _type: "DESTINATION_TARGET",
            type: "PROVIDER_HOME",
            providerSlug: "bauergarden-i-bunn" as ProviderSlug,
        },
    ],
    // [
    //     "zeta",
    //     {
    //         _type: "DESTINATION_TARGET",
    //         type: "PROVIDER_HOME",
    //         providerSlug: "boman" as ProviderSlug,
    //     },
    // ],
    towniTarget,
]);

// Verify that all destinations have a target
if (
    Object.keys(destinations).some(destinationName => {
        return !destinationTargets.has(destinationName as DestinationName);
    })
) {
    const missing = Object.keys(destinations).filter(
        destinationName =>
            !destinationTargets.has(destinationName as DestinationName),
    );
    throw new Error("Missing destination targets; " + missing.join(", "));
}

const providersWithSubDomain = {
    lacko: "https://lacko.towni.se",
    dafto: "https://dafto.towni.se",
    kronocamping: "https://kronocamping.towni.se",
} as Record<ProviderSlug, string>;

const hasProviderDestination = {
    lacko: true,
    dafto: true,
    kronocamping: true,
    "naven-outdoor": true,
    "bauergarden-i-bunn": true,
} as Record<ProviderSlug, string>;

export {
    destinationNameZodSchema,
    destinations,
    destinationTargets,
    hasProviderDestination,
    providersWithSubDomain,
    towniTarget,
};
export type { DestinationName, DestinationTarget };
